import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  #lastValue;

  connectedCallback() {
    super.connectedCallback();
    this.#lastValue = parseInt(this.refs.quantityInput.value);
    this.#toggleButtons();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    this.#toggleButtons();

    const newValue = parseInt(quantityInput.value);

    // Prevent redundant updates (fix "jumping" when already at min/max)
    if (newValue === this.#lastValue) return;
    this.#lastValue = newValue;

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Disables/enables buttons based on min/max rules
   */
  #toggleButtons() {
    const { quantityInput, quantityButtons } = this.refs;
    if (!quantityInput || !Array.isArray(quantityButtons)) return;

    const [minusButton, plusButton] = quantityButtons;
    const value = parseInt(quantityInput.value);
    const min = isNaN(parseInt(quantityInput.min)) ? 1 : parseInt(quantityInput.min);
    const max = parseInt(quantityInput.max);

    if (minusButton) {
      minusButton.toggleAttribute('disabled', value <= min);
    }

    if (plusButton) {
      plusButton.toggleAttribute('disabled', value >= max && !isNaN(max));
    }
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const value = parseFloat(quantityInput.value);
    const min = parseFloat(quantityInput.min);
    const max = parseFloat(quantityInput.max);

    if (!isNaN(min) && value < min) quantityInput.value = min;
    if (!isNaN(max) && value > max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
